import { 
  ClipboardList, 
  Eye, 
  FileCheck, 
  CreditCard, 
  Stethoscope, 
  Home as HomeIcon,
  AlertCircle 
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const steps = [
  {
    icon: ClipboardList,
    title: "1. Information",
    description: "Suivez-nous sur Facebook et Instagram pour être informé des saillies et naissances. Vous pouvez aussi vous inscrire sur notre liste d'attente.",
  },
  {
    icon: Eye,
    title: "2. Visite",
    description: "Prenez rendez-vous pour une visite à partir de la 6ème semaine. Mesures d'hygiène obligatoires : lavage des mains et surchaussures.",
  },
  {
    icon: FileCheck,
    title: "3. Réservation",
    description: "Versement d'arrhes de 30% pour réserver votre chaton. Un contrat de réservation vous sera remis.",
  },
  {
    icon: Stethoscope,
    title: "4. Soins & Stérilisation",
    description: "Vaccinations, vermifugation et stérilisation précoce sont réalisés avant le départ. Votre chaton sera prêt vers 13-14 semaines.",
  },
  {
    icon: HomeIcon,
    title: "5. Accueil",
    description: "Venez chercher votre chaton avec une caisse de transport adaptée. Un kit de départ et des conseils vous seront fournis.",
  },
];

const faqItems = [
  {
    question: "Pourquoi la stérilisation précoce est-elle obligatoire ?",
    answer: "La stérilisation précoce (vers 3 mois) préserve la race Sibérienne en évitant les reproductions non contrôlées. Cette pratique, courante aux États-Unis et en Belgique, n'a pas d'effets secondaires observés et garantit des chats plus équilibrés.",
  },
  {
    question: "Quels sont les vices rédhibitoires couverts ?",
    answer: "La loi vous protège contre : la Panleucopénie (typhus), la Leucose féline, la Péritonite féline et l'Immunodéficience féline. Un certificat vétérinaire dans les délais légaux permet l'annulation de la vente ou le remboursement.",
  },
  {
    question: "Les chatons sont-ils vaccinés ?",
    answer: "Oui, les chatons reçoivent leur premier vaccin typhus/coryza à 2 mois et le rappel à 3 mois. Le vaccin leucose peut être ajouté sur demande pour les futurs chats d'extérieur (supplément d'environ 40€).",
  },
  {
    question: "Quelle alimentation est donnée aux chatons ?",
    answer: "Dès 6 semaines, les chatons sont nourris avec des croquettes de qualité, de la pâtée et des friandises naturelles. Un échantillonnage est fourni au départ pour faciliter la transition.",
  },
  {
    question: "Et si je suis allergique ?",
    answer: "Prévenez-nous en amont de la visite : nous prévoyons plus de temps pour observer vos réactions. Munissez-vous d'antihistaminiques par précaution. Bien que le Sibérien soit souvent toléré, une réaction est toujours possible.",
  },
];

export default function AdoptionSection() {
  return (
    <section
      id="adoption"
      data-testid="adoption-section"
      className="relative py-24 lg:py-32 bg-cream-alt overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section header */}
        <div className="section-animate text-center mb-16">
          <span className="font-accent text-autumn text-xl">Le parcours</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-forest mt-2 mb-6">
            Adopter un Chaton
          </h2>
          <p className="font-body text-lg text-forest/70 max-w-3xl mx-auto">
            L'adoption est un acte responsable. Voici les étapes pour accueillir
            votre futur compagnon dans les meilleures conditions.
          </p>
        </div>

        {/* Steps timeline */}
        <div className="section-animate relative mb-20">
          {/* Connection line - desktop only */}
          <div className="hidden lg:block absolute top-20 left-0 right-0 h-0.5 bg-sand" />
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {steps.map((step, index) => (
              <div
                key={step.title}
                className="relative bg-white rounded-2xl p-6 shadow-soft hover:-translate-y-1 transition-transform duration-300"
              >
                {/* Icon circle */}
                <div className="w-14 h-14 bg-forest rounded-full flex items-center justify-center mb-4 mx-auto lg:mx-0">
                  <step.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-forest mb-2 text-center lg:text-left">
                  {step.title}
                </h3>
                <p className="font-body text-sm text-forest/70 text-center lg:text-left">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Two columns: Important note + FAQ */}
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Important note */}
          <div className="section-animate">
            <div className="bg-autumn/10 border border-autumn/20 rounded-2xl p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-autumn/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-6 h-6 text-autumn" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-forest mb-3">
                    À savoir avant l'adoption
                  </h3>
                  <ul className="font-body text-sm text-forest/80 space-y-3">
                    <li className="flex items-start gap-2">
                      <span className="text-autumn">•</span>
                      Les arrhes versées sont non remboursables si l'acheteur se désiste (art. 1589 du code civil)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-autumn">•</span>
                      Une assurance "premiers pas" Agria de 15 jours est offerte
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-autumn">•</span>
                      Les visites durent 1 à 2 heures : prévoyez du temps pour toutes vos questions
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-autumn">•</span>
                      Délai de 48h après visite pour confirmer votre réservation
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Price info placeholder */}
            <div className="mt-6 bg-forest text-white rounded-2xl p-6">
              <h4 className="font-heading text-lg font-semibold mb-3">
                Tarif et ce qui est inclus
              </h4>
              <ul className="font-body text-sm text-cream/90 space-y-2">
                <li>✓ Identification par puce électronique</li>
                <li>✓ Vaccins typhus/coryza + rappel</li>
                <li>✓ Stérilisation chirurgicale</li>
                <li>✓ Certificat vétérinaire de bonne santé</li>
                <li>✓ Pedigree LOOF</li>
                <li>✓ Kit de départ (alimentation, jouet)</li>
                <li>✓ Assurance santé 15 jours</li>
              </ul>
              <p className="font-body text-xs text-sand/70 mt-4 italic">
                Contactez-nous pour connaître nos tarifs actuels.
              </p>
            </div>
          </div>

          {/* FAQ */}
          <div className="section-animate">
            <h3 className="font-heading text-2xl font-semibold text-forest mb-6">
              Questions fréquentes
            </h3>
            <Accordion type="single" collapsible className="space-y-3">
              {faqItems.map((item, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white rounded-xl border border-sand/50 px-6 overflow-hidden"
                >
                  <AccordionTrigger 
                    className="font-body font-semibold text-forest hover:text-autumn py-4 text-left"
                    data-testid={`faq-trigger-${index}`}
                  >
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-forest/80 pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}
